import useApi from '../../hooks/useApi';

const API_URL = '/api/offers';
const API_URL_BULK = '/api/offers/bulk';

export default function useOffer() {
  const { api: apiClient, createApiCall } = useApi();

  const getOffers = createApiCall(
    async ({ signal }, page, limit, sortColumn, sort, filterModel, startDate, endDate) => {
      const params = {};

      if (page && limit) {
        params.page = page;
        params.limit = limit;
      }
      if (sortColumn && sort) {
        params.sortColumn = sortColumn;
        params.sort = sort;
      }
      if (filterModel && Object.keys(filterModel).length > 0) {
        params.filter = JSON.stringify(filterModel);
      }
      if (startDate) params.startDate = startDate;
      if (endDate) params.endDate = endDate;

      const response = await apiClient.get(API_URL, { params, signal });
      const offers = response.data.offers;

      if (page && limit) {
        const totalCount =
          response.data.totalCount !== undefined
            ? response.data.totalCount
            : offers.length;
        return { offers, totalCount };
      }
      return offers;
    }
  );

  const getAllOffersForIronDome = createApiCall(async ({ signal }, name = '', offerId = '') => {
    const {
      data: { offers },
    } = await apiClient.post(`${API_URL}/iron-dome`, { name, offerId }, { signal });

    return offers;
  });

  const editOffer = createApiCall(
    async ({ signal }, { offerId, editField }) => {
      const {
        data: { offer },
      } = await apiClient.patch(`${API_URL}/${offerId}`, editField, { signal });

      return offer;
    }
  );

  const checkOfferIfExist = createApiCall(async ({ signal }, uniqueId) => {
    const {
      data: { offer },
    } = await apiClient.get(`${API_URL}/by-uid/${uniqueId}`, {
      signal,
    });

    return offer;
  });

  const checkCampaignIfExist = createApiCall(async ({ signal }, uniqueId) => {
    const {
      data: { offer },
    } = await apiClient.get(`${API_URL}/bycampaign-uid/${uniqueId}`, {
      signal,
    });

    return offer;
  });

  const specificEditOffer = createApiCall(
    async ({ signal }, offerId, editField) => {
      const {
        data: { offer },
      } = await apiClient.patch(`${API_URL}/${offerId}`, editField, { signal });

      return offer;
    }
  );

  const editBulkOffer = createApiCall(async ({ signal }, bulkEditOffer) => {
    const data = await apiClient.put(
      `${API_URL}/bulk-edit`,
      { bulkEditOffer },
      {
        signal,
      }
    );
    return data;
  });

  const deleteOffer = createApiCall(async ({ signal }, offerId) => {
    await apiClient.delete(`${API_URL}/${offerId}`, { signal });
  });

  const addOffer = createApiCall(async ({ signal }, newOffer) => {
    const {
      data: { offer },
    } = await apiClient.post(API_URL, newOffer, { signal });
    return offer;
  });

  const cloneOffer = createApiCall(async ({ signal }, newOffer) => {
    const {
      data: { offer },
    } = await apiClient.post(`${API_URL}/clone-offer`, newOffer, { signal });
    return offer;
  });

  const importOffer = createApiCall(async ({ signal }, newOffer) => {
    const {
      data: { offers },
    } = await apiClient.post(API_URL_BULK, { offers: newOffer }, { signal });
    return offers;
  });

  const bulkArchiveOffer = createApiCall(async ({ signal }, ids, archive) => {
    const data = await apiClient.put(
      `${API_URL}/bulk-archive`,
      { ids, archive },
      { signal }
    );

    return data;
  });

  const bulkStatusOffer = createApiCall(async ({ signal }, ids, status) => {
    return apiClient.put(
      `${API_URL}/bulk-status`,
      { ids, status },
      { signal }
    );
  });

  const singleStatusOffer = createApiCall(async ({ signal }, id, status) => {
    return apiClient.put(
      `${API_URL}/${id}/status`,
      { status },
      { signal }
    );
  });

  const bulkEditRules = createApiCall(
    async ({ signal }, { offerIds, editFields }) => {
      const bulkUpdates = offerIds.map((offerId) => ({
        updateOne: {
          filter: { _id: offerId },
          update: { $set: editFields[offerId] },
        },
      }));

      const {
        data: { offers },
      } = await apiClient.put(
        `${API_URL}/bulk-edit-rules`,
        { bulkUpdates },
        { signal }
      );

      return offers;
    }
  );

  const ironDomeBulkImport = createApiCall(
    async ({ signal }, csvData) => {
      const response = await apiClient.post(
        `${API_URL}/iron-dome-bulk-import`,
        { csvData },
        { signal }
      );

      return response.data;
    }
  );

  return {
    addOffer,
    bulkEditRules,
    getOffers,
    editOffer,
    deleteOffer,
    importOffer,
    bulkArchiveOffer,
    bulkStatusOffer,
    singleStatusOffer,
    specificEditOffer,
    editBulkOffer,
    getAllOffersForIronDome,
    checkOfferIfExist,
    checkCampaignIfExist,
    cloneOffer,
    ironDomeBulkImport
  };
}
